import * as THREE from 'three'
import {
  GltfMemoryBudgetExceededError,
  MeshAssetLoadError,
  MeshLoadCancelledError,
  throwIfMeshLoadCancelled,
} from '@/plugins/mesh/errors'
import { GlbMeshLoader } from '@/plugins/mesh/loaders/GltfMeshLoader'
import { applyGltfResourcePlan } from '@/plugins/mesh/loaders/gltfLowMemory/applyGltfResourcePlan'
import { estimateThreeTextureBytes } from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import {
  collectGltfPreflightAsset,
  type GltfPreflightAsset,
  hasGltfResourceSnapshot,
  planGltfResources,
} from '@/plugins/mesh/loaders/gltfLowMemory/gltfResourcePreflight'
import type { GltfManifestReader } from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import {
  createMeshProgressReporter,
  describeMeshSource,
  getMeshSourceExtension,
  loadMeshSourceData,
  resolveMeshBaseUrl,
} from '@/plugins/mesh/loaders/loaderUtils'
import { MeshTextureTaskPool } from '@/plugins/mesh/loaders/MeshTextureTaskPool'
import type { MeshLoadContext } from '@/plugins/mesh/loaders/meshLoadContext'
import {
  normalizeMaxConcurrentLoads,
  parseMeshResultManifest as parseMeshResult,
} from '@/plugins/mesh/loaders/meshResultManifest'
import { enumerateMaterialTextures, MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import { resolveMeshOptimizationOptions } from '@/plugins/mesh/optimization/optimizationOptions'
import type {
  GltfLoaderAdoptedPath,
  GltfLoadingOptionsInput,
  GltfTextureSizingReport,
  MeshKtx2Loader,
  MeshLoadDiagnostics,
  MeshLoadRequest,
  MeshLoadResult,
  MeshLoadStats,
  MeshOptimizationOptionsInput,
  MeshPrimitive,
  MeshResultJsonLoadingOptions,
} from '@/plugins/mesh/types'
import type { IDracoDecodeService } from '@/shared/draco/DracoDecodeService'
import { isDracoGeometry } from '@/shared/draco/gltfBridge'
import { getLoggerManager } from '@/shared/logging'
import type { AssetLoader } from '@/shared/types/assets'

const RESULT_FORMAT = 'mesh-result-json'
const log = getLoggerManager().getLogger('mesh:result-json')

export interface MeshResultJsonLoaderOptions {
  /** 显式借用共享 Draco 服务，并传递给默认子 GLB Loader。 */
  draco?: IDracoDecodeService
  loadingManager?: THREE.LoadingManager
  /** 宿主拥有的已初始化转码器，传递给内置子 GLB Loader。 */
  ktx2?: MeshKtx2Loader
  /** 子 GLB Loader 的 glTF 默认配置。 */
  loading?: GltfLoadingOptionsInput
  /** 子 GLB Loader 的加载前优化配置。 */
  optimization?: MeshOptimizationOptionsInput
  /** 测试或宿主定制使用的子 GLB Loader。 */
  glbLoader?: AssetLoader<MeshLoadRequest, MeshLoadResult>
  /** 自动档位预读子 GLB 纹理数量使用的 Manifest Reader。 */
  manifestReader?: GltfManifestReader
  /** 同时加载的子 GLB 数，默认 2，运行时限制为 1..8。 */
  maxConcurrentLoads?: MeshResultJsonLoadingOptions['maxConcurrentLoads']
}

function createStats(primitives: readonly MeshPrimitive[]): MeshLoadStats {
  const materials = new Set(primitives.map((primitive) => primitive.material.uuid))
  return {
    format: RESULT_FORMAT,
    primitiveCount: primitives.length,
    triangleCount: primitives.reduce((sum, primitive) => sum + primitive.triangleCount, 0),
    vertexCount: primitives.reduce((sum, primitive) => sum + primitive.vertexCount, 0),
    materialCount: materials.size,
    opaqueCount: primitives.filter((primitive) => primitive.renderClass === 'opaque').length,
    maskCount: primitives.filter((primitive) => primitive.renderClass === 'mask').length,
    blendCount: primitives.filter((primitive) => primitive.renderClass === 'blend').length,
  }
}

function disposeResults(results: readonly (MeshLoadResult | undefined)[]): void {
  for (const result of results) {
    if (!result) continue
    try {
      result.dispose()
    } catch {
      // 保留触发清理的原始加载错误。
    }
  }
}

async function runBoundedTasks(
  taskCount: number,
  concurrency: number,
  task: (index: number) => Promise<void>,
  onFirstError?: (error: unknown) => void
): Promise<void> {
  let nextIndex = 0
  let failed = false
  const workers = Array.from({ length: Math.min(taskCount, concurrency) }, async () => {
    while (!failed) {
      const index = nextIndex
      if (index >= taskCount) return
      nextIndex += 1
      try {
        await task(index)
      } catch (error) {
        if (!failed) {
          failed = true
          onFirstError?.(error)
        }
        throw error
      }
    }
  })
  await Promise.all(workers)
}

function prefixPart(part: MeshLoadResult, prefix: string): MeshLoadResult {
  const primitives = part.primitives.map((primitive) => ({
    ...primitive,
    id: `${prefix}#${primitive.id}`,
    sourceNodeId: `${prefix}#${primitive.sourceNodeId}`,
    pickGroupId: prefix,
  }))
  const sourcePrimitiveMap = new Map<string, MeshPrimitive[]>()
  for (const primitive of primitives) {
    const matches = sourcePrimitiveMap.get(primitive.sourceNodeId) ?? []
    matches.push(primitive)
    sourcePrimitiveMap.set(primitive.sourceNodeId, matches)
  }
  return { ...part, primitives, sourcePrimitiveMap }
}

function createLinkedAbortController(signal?: AbortSignal): {
  controller: AbortController
  unlink: () => void
} {
  const controller = new AbortController()
  const abort = () => controller.abort(signal?.reason)
  if (signal?.aborted) abort()
  else signal?.addEventListener('abort', abort, { once: true })
  return {
    controller,
    unlink: () => signal?.removeEventListener('abort', abort),
  }
}

function getChildMemory(result: MeshLoadResult): {
  geometryBytes: number
  rangeBytes: number
  decodeBytes: number
} {
  const memory = result.report.lowMemory?.memory
  const arrays = new Set<ArrayBufferView>()
  const addAttribute = (attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) => {
    arrays.add('data' in attribute ? attribute.data.array : attribute.array)
  }
  for (const geometry of result.resources.geometries) {
    for (const attribute of Object.values(geometry.attributes)) addAttribute(attribute)
    if (geometry.index) addAttribute(geometry.index)
    for (const attributes of Object.values(geometry.morphAttributes)) {
      for (const attribute of attributes) addAttribute(attribute)
    }
  }
  for (const primitive of result.primitives) {
    if (!(primitive.object instanceof THREE.InstancedMesh)) continue
    addAttribute(primitive.object.instanceMatrix)
    if (primitive.object.instanceColor) addAttribute(primitive.object.instanceColor)
  }
  const measuredGeometryBytes = Array.from(arrays).reduce((sum, array) => sum + array.byteLength, 0)
  return {
    geometryBytes: Math.max(measuredGeometryBytes, memory?.actualAllocatedBytes ?? 0),
    rangeBytes: memory?.peakRangeCacheBytes ?? 0,
    decodeBytes: memory?.peakTextureDecodeBytes ?? 0,
  }
}

function getCommonAdoptedPath(
  results: readonly MeshLoadResult[]
): GltfLoaderAdoptedPath | undefined {
  const first = results[0]?.report.adoptedPath
  return first && results.every((result) => result.report.adoptedPath === first) ? first : undefined
}

/** 加载 mesh_result.json 引用的分块 GLB，并聚合为单一静态 Mesh 结果。 */
export class MeshResultJsonLoader implements AssetLoader<MeshLoadRequest, MeshLoadResult> {
  readonly format = 'json'
  readonly detectPriority = 20

  private readonly glbLoader: AssetLoader<MeshLoadRequest, MeshLoadResult>
  private readonly loading?: GltfLoadingOptionsInput
  private readonly _optimization?: MeshOptimizationOptionsInput
  private readonly manifestReader?: GltfManifestReader
  private readonly maxConcurrentLoads: number
  private readonly _hasKtx2: boolean
  private readonly _draco?: IDracoDecodeService

  constructor(options: MeshResultJsonLoaderOptions = {}) {
    this._hasKtx2 = options.ktx2 !== undefined
    this._draco = options.draco
    this.loading = options.loading
    this._optimization = options.optimization
    this.maxConcurrentLoads = normalizeMaxConcurrentLoads(options.maxConcurrentLoads)
    this.manifestReader = options.manifestReader
    this.glbLoader =
      options.glbLoader ??
      new GlbMeshLoader({
        loading: options.loading,
        optimization: options.optimization,
        ktx2: options.ktx2,
        draco: options.draco,
        loadingManager: options.loadingManager,
      })
  }

  canLoad(request: MeshLoadRequest): boolean {
    if (request.format) return request.format.toLowerCase() === this.format
    if (request.url?.split(/[?#]/, 1)[0].toLowerCase().endsWith('/mesh_result.json')) return true
    if (getMeshSourceExtension(request.url) !== 'json' || typeof request.data !== 'string') {
      return false
    }
    return request.data.includes('"glb_model_list"')
  }

  async load(request: MeshLoadRequest, context: MeshLoadContext): Promise<MeshLoadResult> {
    const source = describeMeshSource(request)
    const requestSignal = context.signal ?? request.signal
    const dracoService = this._draco ?? context.dracoService
    const linked = createLinkedAbortController(requestSignal)
    const signal = linked.controller.signal
    const reportProgress = createMeshProgressReporter(request, context)
    const startedAt = getLoggerManager().now()
    let childResults: (MeshLoadResult | undefined)[] = []
    let manifestMs = 0
    let preflightMs = 0
    let childLoadsMs = 0
    let maxConcurrentLoads = normalizeMaxConcurrentLoads(
      request.meshResult?.maxConcurrentLoads ?? this.maxConcurrentLoads
    )
    log.debug(
      `event=mesh.result-json.load.start source=${source} childConcurrency=${maxConcurrentLoads}`
    )

    try {
      reportProgress(0)
      const manifestStartedAt = getLoggerManager().now()
      const data = await loadMeshSourceData(request, context, (progress) =>
        reportProgress(progress * 0.05)
      )
      throwIfMeshLoadCancelled(signal, source)
      const baseUrl = resolveMeshBaseUrl(request)
      if (!baseUrl) throw new Error('mesh_result.json 使用相对 GLB 路径时必须提供 url 或 baseUrl')
      const manifest = parseMeshResult(data, baseUrl)
      const referenceCounts = new Map<string, number>()
      for (const child of manifest.glbs)
        referenceCounts.set(child.reference, (referenceCounts.get(child.reference) ?? 0) + 1)
      const sourcePrefixes = manifest.glbs.map((child, index) =>
        referenceCounts.get(child.reference)! > 1 ? `${child.reference}@${index}` : child.reference
      )
      childResults = new Array<MeshLoadResult | undefined>(manifest.glbs.length)
      manifestMs = getLoggerManager().now() - manifestStartedAt

      let loading = resolveGltfLoadingOptions(this.loading, request.gltf)
      let textureSizing: GltfTextureSizingReport | undefined
      let childProgressStart = 0.05
      const prefetchedChildData = new Map<string, MeshLoadRequest['data']>()
      if (loading.texture.maxSize === 'auto' || hasGltfResourceSnapshot(loading)) {
        const preflightStartedAt = getLoggerManager().now()
        const assets: GltfPreflightAsset[] = new Array(manifest.glbs.length)
        const preflightProgress = new Array<number>(manifest.glbs.length).fill(0)
        await runBoundedTasks(
          manifest.glbs.length,
          maxConcurrentLoads,
          async (index) => {
            const child = manifest.glbs[index]
            throwIfMeshLoadCancelled(signal, source)
            const childRequest: MeshLoadRequest = {
              ...request,
              url: child.url,
              data: undefined,
              format: 'glb',
              baseUrl: undefined,
              gltf: loading,
              signal,
              optimization: resolveMeshOptimizationOptions(
                this._optimization,
                request.optimization
              ),
              onProgress: undefined,
              onPreflight: undefined,
              coordinate: undefined,
            }
            const asset = await collectGltfPreflightAsset(
              childRequest,
              'glb',
              loading,
              await this.manifestReader?.read(childRequest, 'glb', signal),
              this._hasKtx2 || context.ktx2Service !== undefined,
              dracoService !== undefined
            )
            throwIfMeshLoadCancelled(signal, source)
            assets[index] = asset
            if (asset.document.sourceData !== undefined) {
              prefetchedChildData.set(child.url, asset.document.sourceData)
            }
            preflightProgress[index] = 1
            reportProgress(
              0.05 +
                (preflightProgress.reduce((sum, value) => sum + value, 0) / manifest.glbs.length) *
                  0.1
            )
          },
          (error) => linked.controller.abort(error)
        )
        preflightMs = getLoggerManager().now() - preflightStartedAt
        const resources = planGltfResources(
          assets,
          loading,
          request.gltf?.budget?.maxTextureBytes ?? this.loading?.budget?.maxTextureBytes,
          maxConcurrentLoads,
          preflightMs,
          context.getLoadedMeshGpuBytes?.()
        )
        request.onPreflight?.(resources)
        const resolution = applyGltfResourcePlan(loading, resources, source)
        loading = resolution.loading
        textureSizing = resolution.report
        maxConcurrentLoads = resources.selected!.loadConcurrency
        childProgressStart = 0.15
      }
      const textures = new Set<THREE.Texture>()
      let estimatedTextureBytes = 0
      const textureTaskPool =
        context.textureTaskPool ?? new MeshTextureTaskPool(loading.texture.maxConcurrentDecodes)
      const checkTextures = (candidates: Iterable<THREE.Texture>) => {
        for (const texture of candidates) {
          if (textures.has(texture)) continue
          textures.add(texture)
          estimatedTextureBytes += estimateThreeTextureBytes(texture)
        }
        if (estimatedTextureBytes > loading.budget.maxTextureBytes) {
          const error = new GltfMemoryBudgetExceededError(
            source,
            'budget-texture-exceeded',
            estimatedTextureBytes,
            loading.budget.maxTextureBytes
          )
          linked.controller.abort(error)
          throw error
        }
      }
      const geometryArrays = new Set<ArrayBufferView>()
      let observedGeometryBytes = 0
      let hasDracoGeometry = false
      const checkGeometry = (geometries: Iterable<THREE.BufferGeometry>) => {
        if (!dracoService) return
        for (const geometry of geometries) {
          hasDracoGeometry ||= isDracoGeometry(geometry)
          for (const attribute of [...Object.values(geometry.attributes), geometry.index]) {
            if (!attribute) continue
            const array = 'data' in attribute ? attribute.data.array : attribute.array
            if (geometryArrays.has(array)) continue
            geometryArrays.add(array)
            observedGeometryBytes += array.byteLength
          }
        }
        if (hasDracoGeometry && observedGeometryBytes > loading.budget.maxFinalGeometryBytes) {
          const error = new GltfMemoryBudgetExceededError(
            source,
            'budget-final-exceeded',
            observedGeometryBytes,
            loading.budget.maxFinalGeometryBytes
          )
          linked.controller.abort(error)
          throw error
        }
      }
      const childLoadsStartedAt = getLoggerManager().now()
      const childProgress = new Array<number>(manifest.glbs.length).fill(0)
      let activeLoads = 0
      let peakConcurrentLoads = 0
      await runBoundedTasks(
        manifest.glbs.length,
        maxConcurrentLoads,
        async (index) => {
          const child = manifest.glbs[index]
          throwIfMeshLoadCancelled(signal, source)
          activeLoads += 1
          peakConcurrentLoads = Math.max(peakConcurrentLoads, activeLoads)
          const deliveredPrimitives = new Set<string>()
          const prefix = sourcePrefixes[index]
          const childContext: MeshLoadContext = {
            loadingManager: context.loadingManager,
            ktx2Service: context.ktx2Service,
            dracoService,
            resourcePlanApplied: textureSizing !== undefined,
            signal,
            textureTaskPool,
            onPart: context.onPart
              ? (part) => {
                  throwIfMeshLoadCancelled(signal, source)
                  if (part.primitives.length === 0) return
                  for (const primitive of part.primitives) {
                    if (deliveredPrimitives.has(primitive.id))
                      throw new Error(`子 GLB 重复交付 primitive: ${child.reference}`)
                    deliveredPrimitives.add(primitive.id)
                  }
                  for (const primitive of part.primitives)
                    checkTextures(enumerateMaterialTextures(primitive.material))
                  checkGeometry(part.resources.geometries)
                  context.onPart!(prefixPart(part, prefix))
                }
              : undefined,
            onProgress: (progress) => {
              childProgress[index] = Math.max(childProgress[index], progress)
              reportProgress(
                childProgressStart +
                  (childProgress.reduce((sum, value) => sum + value, 0) / manifest.glbs.length) *
                    (1 - childProgressStart)
              )
            },
          }
          try {
            const prefetched = prefetchedChildData.get(child.url)
            prefetchedChildData.delete(child.url)
            const childResult = await this.glbLoader.load(
              {
                ...request,
                id: request.id ? `${request.id}:block-${index}` : undefined,
                name: child.reference,
                url: child.url,
                data: prefetched,
                format: 'glb',
                baseUrl: undefined,
                gltf: loading,
                signal,
                onProgress: undefined,
                onPartialLoad: undefined,
                coordinate: undefined,
              },
              childContext
            )
            if (signal?.aborted) {
              childResult.dispose()
              throw new MeshLoadCancelledError(source, { cause: signal.reason })
            }
            childResults[index] = childResult
            checkGeometry(childResult.resources.geometries)
            childProgress[index] = 1
            checkTextures(childResult.resources.textures)
            if (deliveredPrimitives.size === 0) context.onPart?.(prefixPart(childResult, prefix))
            else if (deliveredPrimitives.size !== childResult.primitives.length) {
              throw new Error(
                `子 GLB 渐进交付不完整: ${child.reference}，已交付 ${deliveredPrimitives.size}，完整 ${childResult.primitives.length}`
              )
            }
          } finally {
            activeLoads -= 1
          }
        },
        (error) => linked.controller.abort(error)
      )
      childLoadsMs = getLoggerManager().now() - childLoadsStartedAt
      const completedChildResults = childResults.filter(
        (result): result is MeshLoadResult => result !== undefined
      )
      const cumulativeChildMs = completedChildResults.reduce(
        (sum, result) => sum + result.report.elapsedMs,
        0
      )
      const slowestChild = completedChildResults.reduce(
        (slowest, result, index) =>
          !slowest || result.report.elapsedMs > slowest.result.report.elapsedMs
            ? { index, result }
            : slowest,
        undefined as { index: number; result: MeshLoadResult } | undefined
      )
      const childMemory = completedChildResults.map(getChildMemory)
      const finalGeometryBytes = childMemory.reduce((sum, memory) => sum + memory.geometryBytes, 0)
      const transientUpperBounds = childMemory
        .map((memory) => memory.rangeBytes + memory.decodeBytes)
        .sort((left, right) => right - left)
        .slice(0, maxConcurrentLoads)
      const concurrentTransientBytes = transientUpperBounds.reduce((sum, bytes) => sum + bytes, 0)
      const estimatedGpuBytes = finalGeometryBytes + estimatedTextureBytes

      const resources = new MeshResourceTracker()
      const root = new THREE.Group()
      root.name = request.name ?? 'mesh_result.json'
      root.matrixAutoUpdate = false
      const primitives: MeshPrimitive[] = []
      const sourcePrimitiveMap = new Map<string, readonly MeshPrimitive[]>()
      const boundingBox = new THREE.Box3()
      const ignoredAnimations: string[] = []
      const warnings = [`分块清单加载了 ${childResults.length} 个 GLB`]

      for (const [index, child] of completedChildResults.entries()) {
        const path = sourcePrefixes[index]
        child.root.name = path
        root.add(child.root)
        resources.adopt(child.resources)
        boundingBox.union(child.boundingBox)
        const remapped = new Map<MeshPrimitive, MeshPrimitive>()
        for (const primitive of child.primitives) {
          const mapped: MeshPrimitive = {
            ...primitive,
            id: `${path}#${primitive.id}`,
            sourceNodeId: `${path}#${primitive.sourceNodeId}`,
            pickGroupId: path,
          }
          remapped.set(primitive, mapped)
          primitives.push(mapped)
        }
        for (const [sourceNodeId, matches] of child.sourcePrimitiveMap) {
          sourcePrimitiveMap.set(
            `${path}#${sourceNodeId}`,
            matches.map((primitive) => remapped.get(primitive)!).filter(Boolean)
          )
        }
        ignoredAnimations.push(
          ...child.diagnostics.ignoredAnimations.map((name) => `${path}#${name}`)
        )
        warnings.push(...child.diagnostics.warnings.map((warning) => `${path}: ${warning}`))
      }

      const diagnostics: MeshLoadDiagnostics = { ignoredAnimations, warnings }
      const elapsedMs = getLoggerManager().now() - startedAt
      reportProgress(1)
      log.debug(
        `event=mesh.result-json.load.complete source=${source} elapsedMs=${elapsedMs.toFixed(
          1
        )} manifestMs=${manifestMs.toFixed(1)} preflightMs=${preflightMs.toFixed(
          1
        )} childLoadsMs=${childLoadsMs.toFixed(1)} children=${
          completedChildResults.length
        } primitives=${primitives.length} configuredConcurrency=${maxConcurrentLoads} peakConcurrency=${peakConcurrentLoads} cumulativeChildMs=${cumulativeChildMs.toFixed(
          1
        )} parallelSpeedup=${(cumulativeChildMs / Math.max(childLoadsMs, Number.EPSILON)).toFixed(
          2
        )} slowestChild=${slowestChild ? manifest.glbs[slowestChild.index].url : '(none)'} slowestChildMs=${
          slowestChild?.result.report.elapsedMs.toFixed(1) ?? '0.0'
        } finalGeometryBytes=${finalGeometryBytes} finalTextureBytes=${estimatedTextureBytes} estimatedGpuBytes=${estimatedGpuBytes} concurrentTransientBytes=${concurrentTransientBytes} estimatedPeakWorkingSetBytes=${
          estimatedGpuBytes + concurrentTransientBytes
        } estimateKind=conservative-engine-estimate`
      )
      return {
        root,
        primitives,
        format: RESULT_FORMAT,
        resources,
        diagnostics,
        boundingBox,
        sourcePrimitiveMap,
        report: {
          stats: createStats(primitives),
          diagnostics,
          elapsedMs,
          adoptedPath: getCommonAdoptedPath(completedChildResults),
          textureSizing,
          ktx2: completedChildResults.some((result) => result.report.ktx2)
            ? {
                textureCount: completedChildResults.reduce(
                  (sum, result) => sum + (result.report.ktx2?.textureCount ?? 0),
                  0
                ),
                encodedBytes: completedChildResults.reduce(
                  (sum, result) => sum + (result.report.ktx2?.encodedBytes ?? 0),
                  0
                ),
                transcodedBytes: completedChildResults.reduce(
                  (sum, result) => sum + (result.report.ktx2?.transcodedBytes ?? 0),
                  0
                ),
                outputBytes: completedChildResults.reduce(
                  (sum, result) => sum + (result.report.ktx2?.outputBytes ?? 0),
                  0
                ),
                rgba8FallbackCount: completedChildResults.reduce(
                  (sum, result) => sum + (result.report.ktx2?.rgba8FallbackCount ?? 0),
                  0
                ),
                workerPeakBytes: null,
              }
            : undefined,
        },
        dispose: () => resources.dispose(),
      }
    } catch (cause) {
      const error = signal.reason instanceof GltfMemoryBudgetExceededError ? signal.reason : cause
      linked.controller.abort(error)
      disposeResults(childResults)
      if (
        error instanceof MeshLoadCancelledError ||
        error instanceof GltfMemoryBudgetExceededError
      ) {
        throw error
      }
      log.error(
        `分块清单加载失败，来源: ${source}，原因: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
      throw new MeshAssetLoadError(RESULT_FORMAT, source, error)
    } finally {
      linked.unlink()
    }
  }
}
